import { useCallback, useEffect, useRef, useState } from "react";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import type { SsoButtonsProvider } from "@opero/ui/auth";
import {
  loadSsoConfig,
  startSso,
  type SsoEnforcement,
  type SsoMode,
} from "../lib/sso";

interface UseSsoProvidersOptions {
  isOpen: boolean;
  mode: SsoMode;
  /**
   * Caller-owned error sink. The modals surface SSO failures in the *same*
   * banner state their login/signup submit paths write, so the hook must not
   * keep an error of its own.
   */
  onError: (message: string | null) => void;
}

interface UseSsoProvidersResult {
  providers: SsoButtonsProvider[];
  /** Tenant-wide default — see `loadSsoConfig`. */
  ssoEnforcement: SsoEnforcement;
  redirecting: boolean;
  select: (provider: SsoButtonsProvider) => Promise<void>;
}

const FALLBACK_MESSAGE: Record<SsoMode, string> = {
  login: "Couldn't start SSO sign-in",
  signup: "Couldn't start SSO sign-up",
};

/**
 * Loads the configured SSO providers while a modal is open and drives the
 * redirect handoff.
 */
export function useSsoProviders({
  isOpen,
  mode,
  onError,
}: UseSsoProvidersOptions): UseSsoProvidersResult {
  const [providers, setProviders] = useState<SsoButtonsProvider[]>([]);
  const [ssoEnforcement, setSsoEnforcement] =
    useState<SsoEnforcement>("disabled");
  const [redirecting, setRedirecting] = useState(false);

  // Read through a ref so an unstable caller callback can't re-run the loader.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!isOpen) return;
    setRedirecting(false);
    let cancelled = false;
    loadSsoConfig().then((config) => {
      if (cancelled) return;
      // `logoUrl` is the wire name; `SsoButtons` calls the same thing
      // `iconUrl`. Mapped here rather than renamed in either place.
      setProviders(
        config.providers.map((p) => ({
          connectionId: p.connectionId,
          providerType: p.providerType,
          displayName: p.displayName,
          iconUrl: p.logoUrl,
        })),
      );
      setSsoEnforcement(config.ssoEnforcement);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const select = useCallback(
    async (provider: SsoButtonsProvider) => {
      onErrorRef.current(null);
      setRedirecting(true);
      try {
        await startSso(provider, { mode });
      } catch (err: unknown) {
        setRedirecting(false);
        onErrorRef.current(getErrorMessage(err, FALLBACK_MESSAGE[mode]));
      }
    },
    [mode],
  );

  return { providers, ssoEnforcement, redirecting, select };
}
